import { esc } from '../utils/dom.js';
/**
 * Lightweight info tooltip helper.
 *
 * Use anywhere in the UI to add a small ⓘ icon that reveals a help bubble
 * on hover/focus. Two ways to use:
 *
 *   1. As an HTML helper for templates:
 *        ${helpTip('NCCD level governs how much funding the school can claim.')}
 *
 *   2. As a global delegated handler — any element with `data-tooltip="text"`
 *      gets the same hover bubble. Initialised once at app boot via
 *      setupTooltips().
 */

/** Inline help icon you can drop next to a label. */
export function helpTip(text, opts = {}) {
  if (!text) return '';
  const placement = opts.placement || 'top';
  return `<span class="help-tip" tabindex="0" data-tooltip="${esc(text)}" data-placement="${placement}" aria-label="${esc(text)}">ⓘ</span>`;
}

let _tipEl = null;
function showTip(target) {
  const text = target.getAttribute('data-tooltip');
  if (!text) return;
  if (!_tipEl) {
    _tipEl = document.createElement('div');
    _tipEl.className = 'tooltip-bubble';
    document.body.appendChild(_tipEl);
  }
  _tipEl.textContent = text;
  _tipEl.classList.add('visible');
  // Position above the target by default
  const rect = target.getBoundingClientRect();
  _tipEl.style.visibility = 'hidden';
  _tipEl.style.display = 'block';
  const tipRect = _tipEl.getBoundingClientRect();
  const placement = target.getAttribute('data-placement') || 'top';
  let top, left;
  if (placement === 'top') {
    top = rect.top - tipRect.height - 8 + window.scrollY;
    left = rect.left + rect.width / 2 - tipRect.width / 2 + window.scrollX;
  } else if (placement === 'bottom') {
    top = rect.bottom + 8 + window.scrollY;
    left = rect.left + rect.width / 2 - tipRect.width / 2 + window.scrollX;
  } else if (placement === 'right') {
    top = rect.top + rect.height / 2 - tipRect.height / 2 + window.scrollY;
    left = rect.right + 8 + window.scrollX;
  } else {
    top = rect.top + rect.height / 2 - tipRect.height / 2 + window.scrollY;
    left = rect.left - tipRect.width - 8 + window.scrollX;
  }
  // Clamp to viewport
  const margin = 8;
  if (left < margin) left = margin;
  if (left + tipRect.width > window.innerWidth - margin) left = window.innerWidth - tipRect.width - margin;
  _tipEl.style.top = top + 'px';
  _tipEl.style.left = left + 'px';
  _tipEl.style.visibility = '';
}

function hideTip() {
  if (_tipEl) _tipEl.classList.remove('visible');
}

export function setupTooltips() {
  // Delegated hover/focus listeners — anything with data-tooltip works.
  document.addEventListener('mouseover', (e) => {
    const t = e.target.closest('[data-tooltip]');
    if (t) showTip(t);
  });
  document.addEventListener('mouseout', (e) => {
    const t = e.target.closest('[data-tooltip]');
    if (t) hideTip();
  });
  document.addEventListener('focusin', (e) => {
    const t = e.target.closest('[data-tooltip]');
    if (t) showTip(t);
  });
  document.addEventListener('focusout', (e) => {
    const t = e.target.closest('[data-tooltip]');
    if (t) hideTip();
  });
}
